import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCheck, FiAlertTriangle, FiClock, FiZap, FiRefreshCw, FiUser, FiChevronDown,
  FiList, FiSettings, FiPlus, FiTrendingUp, FiFileText, FiHelpCircle, FiSend,
  FiCalendar, FiArrowLeft, FiChevronRight, FiHome, FiX, FiPhone, FiFilter,
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { getVoiceAgentsDF, getPhoneNumbersDF, type DfVoiceAgent, type DfPhoneNumber } from '../../services/voiceDataFabricService';
import {
  useRateQuoteLink,
  type RunEntry,
  type WorkflowStep,
  type StepStatus,
  type PanelView,
  type RateQuoteLinkSettings,
  type QuoteRow,
  type QuoteResult,
  type ProgressItem,
  type AwaitingInputPrompt,
  type AwaitingReviewPayload,
  type OpportunityRecord,
  type SelectedQuoteRunData,
  type EventLogEntry,
} from './RateQuoteLinkContext';
import { useRunChat } from './useRunChat';

const SIDEBAR_CHAT_SUGGESTIONS = [
  'Submit to carriers',
  'Change effective date',
  'Change BI to 100/300',
  'Set collision deductible to $500',
  'Remove PIP',
];

const ROW_STATUS_LABEL: Record<QuoteRow['status'], string> = {
  pending:       'Pending',
  'in-progress': 'In Progress',
  completed:     'Completed',
  failed:        'Failed',
};

/**
 * Right-sidebar panel for the Rate Quoting Skill — a contextual side workspace
 * with a top tab strip switching between four views:
 *
 *   • Details   — selected row details (avatar, premium, policy, notes, actions)
 *   • Workflow  — live / latest workflow card with step-by-step progress
 *   • History   — execution insights + full timeline of past runs
 *   • Settings  — inline automation configuration form
 */

/* ── helpers ────────────────────────────────────────────────────────────── */

function formatRelative(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const mins   = Math.floor(diffMs / 60_000);
  if (mins < 1)         return 'Just now';
  if (mins < 60)        return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  const days = Math.floor(hours / 24);
  if (days === 1) return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  if (days < 7)   return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDuration(ms?: number): string {
  if (!ms) return '';
  if (ms < 1000)  return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/* ── status meta ───────────────────────────────────────────────────────── */

const STATUS_META: Record<RunEntry['status'], {
  Icon: React.ComponentType<{ size?: number }>;
  label: string;
  color: string;
  bg: string;
  border: string;
}> = {
  running:        { Icon: FiRefreshCw,     label: 'Running',         color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.14)', border: 'rgba(96, 165, 250, 0.40)' },
  awaiting_input: { Icon: FiHelpCircle,    label: 'Needs input',     color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.14)', border: 'rgba(251, 191, 36, 0.40)' },
  completed:      { Icon: FiCheck,         label: 'Completed',       color: '#34d399', bg: 'rgba(52, 211, 153, 0.14)', border: 'rgba(52, 211, 153, 0.40)' },
  failed:         { Icon: FiAlertTriangle, label: 'Failed',          color: '#ef4444', bg: 'rgba(239, 68, 68, 0.14)',  border: 'rgba(239, 68, 68, 0.40)'  },
};

/* ── trigger badge ─────────────────────────────────────────────────────── */

const TriggerBadge: React.FC<{ trigger: RunEntry['trigger']; size?: 'sm' | 'md' }> = ({ trigger, size = 'sm' }) => {
  if (trigger === 'ai') {
    return (
      <span className={`rql-trigger-badge rql-trigger-badge--ai rql-trigger-badge--${size}`}>
        <RiSparklingFill size={size === 'md' ? 12 : 10} />
        AI Executed
      </span>
    );
  }
  return (
    <span className={`rql-trigger-badge rql-trigger-badge--manual rql-trigger-badge--${size}`}>
      <FiUser size={size === 'md' ? 12 : 10} />
      Manual Run
    </span>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   DETAILS VIEW — selected-row detail panel (moved from inline page aside)
   ══════════════════════════════════════════════════════════════════════════ */

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const WIN_PROB_COLOR: Record<string, string> = {
  Excellent: '#4ED56E',
  Good:      '#A3E635',
  Fair:      '#FEAD0F',
  Poor:      '#D62612',
};

/* Skeleton for the Opportunities section while data is in flight */
const SkeletonOpportunities: React.FC = () => (
  <div className="rql-opp-skel">
    {/* Section label */}
    <div className="rql-opp-skel__label">
      <span className="rql-skel rql-skel--icon" style={{ width: 11, height: 11, borderRadius: 2 }} />
      <span className="rql-skel rql-skel--sm" />
    </div>
    {/* 2 placeholder cards */}
    {[0, 1].map(i => (
      <div key={i} className="rql-opp-skel__card">
        <div className="rql-opp-skel__card-head">
          <span className="rql-skel rql-skel--xs" />
          <span className="rql-skel rql-skel--md" />
        </div>
        <div className="rql-opp-skel__card-rows">
          <div className="rql-opp-skel__card-row">
            <span className="rql-skel rql-skel--sm" />
            <span className="rql-skel rql-skel--sm" />
          </div>
          <div className="rql-opp-skel__card-row">
            <span className="rql-skel rql-skel--sm" />
            <span className="rql-skel rql-skel--md" />
          </div>
          <div className="rql-opp-skel__card-row">
            <span className="rql-skel rql-skel--sm" />
            <span className="rql-skel rql-skel--xs" />
          </div>
        </div>
      </div>
    ))}
  </div>
);


/* ── Policy-type picker modal ───────────────────────────────────────────── */
const PolicyTypePicker: React.FC<{
  onSelect: (policyType: 'PAP' | 'HO') => void;
  onClose: () => void;
}> = ({ onSelect, onClose }) => (
  <div className="rql-policy-picker-overlay" onClick={onClose}>
    <div className="rql-policy-picker" onClick={e => e.stopPropagation()}>
      <div className="rql-policy-picker__header">
        <span className="rql-policy-picker__title">Select Policy Type</span>
        <button className="rql-policy-picker__close" onClick={onClose} aria-label="Close">
          <FiX size={14} />
        </button>
      </div>
      <div className="rql-policy-picker__options">
        <button className="rql-policy-picker__opt" onClick={() => onSelect('PAP')}>
          <span className="rql-policy-picker__opt-icon"><FiZap size={18} /></span>
          <span className="rql-policy-picker__opt-body">
            <span className="rql-policy-picker__opt-label">Personal Auto</span>
            <span className="rql-policy-picker__opt-sub">PAP — Drivers &amp; Vehicles</span>
          </span>
        </button>
        <button className="rql-policy-picker__opt" onClick={() => onSelect('HO')}>
          <span className="rql-policy-picker__opt-icon"><FiHome size={18} /></span>
          <span className="rql-policy-picker__opt-body">
            <span className="rql-policy-picker__opt-label">Home Owners</span>
            <span className="rql-policy-picker__opt-sub">HO — Property &amp; Dwelling</span>
          </span>
        </button>
      </div>
    </div>
  </div>
);

const DetailsView: React.FC = () => {
  const {
    selectedRow, setSelectedRow, triggerRun,
    setPanelView, requestOpen,
    opportunities,
  } = useRateQuoteLink();

  const [policyPickerOpen, setPolicyPickerOpen] = useState(false);

  const handleExecute = (policyType: 'PAP' | 'HO') => {
    if (!selectedRow) return;
    setPolicyPickerOpen(false);
    triggerRun({
      rowId: selectedRow.id,
      workflow,
      label: `${workflow === 'new' ? 'New' : 'Renewal'} — ${selectedRow.insured}`,
      insuredName: selectedRow.insured,
      policyType,
    });
    setPanelView('workflow');
    requestOpen();
  };

  if (!selectedRow) {
    return (
      <div className="rql-details-empty">
        <div className="rql-details-empty__halo">
          <div className="rql-details-empty__icon">
            <FiFileText size={26} />
          </div>
        </div>
        <div className="rql-details-empty__title">No quote selected</div>
        <div className="rql-details-empty__hint">
          Click any row from the table to see its full details — premium,
          carrier, policy info, notes &amp; quick actions.
        </div>
        <div className="rql-details-empty__tip">
          <FiZap size={11} />
          <span>Tip: rows are clickable</span>
        </div>
      </div>
    );
  }

  const initials = selectedRow.insured.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  // Map the table's row status to a sensible workflow lane.
  const workflow: 'new' | 'renewal' = selectedRow.id.startsWith('q-r-') ? 'renewal' : 'new';
  // Prospect rows (New tab) have no carrier/premium — hide those sections.
  const isProspect = selectedRow.carrier === '—' && selectedRow.premium === '—' && selectedRow.date === '—';

  return (
    <div className="rql-details">
      <div className="rql-details__identity">
        <div className="rql-details__avatar" aria-hidden="true" title={selectedRow.insured}>
          {initials}
        </div>
        <div className="rql-details__identity-text">
          <div className="rql-details__insured">{selectedRow.insured}</div>
          {selectedRow.lineOfBusiness && (
            <div className="rql-details__lob">{selectedRow.lineOfBusiness}</div>
          )}
        </div>
        <span className={`rql-details__status rql-status rql-status--${selectedRow.status}`}>
          {selectedRow.status === 'completed' && <FiCheck size={11} />}
          {selectedRow.status === 'failed'    && <FiAlertTriangle size={11} />}
          {selectedRow.status === 'pending'   && <FiClock size={11} />}
          {ROW_STATUS_LABEL[selectedRow.status]}
        </span>
      </div>

      {!isProspect && (
        <div className="rql-details__hero">
          <div className="rql-details__hero-label">Premium</div>
          <div className="rql-details__hero-amount">{selectedRow.premium}</div>
          {selectedRow.effectiveDate && (
            <div className="rql-details__hero-meta">
              <FiClock size={11} />
              <span>Effective {selectedRow.effectiveDate}</span>
              {selectedRow.expirationDate && (
                <>
                  <span className="rql-run-hero__dot-sep" />
                  <span>Expires {selectedRow.expirationDate}</span>
                </>
              )}
            </div>
          )}
        </div>
      )}

     
      {!isProspect && (
        <div className="rql-details__section">
          <div className="rql-details__section-label">Policy</div>
          <dl className="rql-details__grid">
            {selectedRow.policyNumber && (
              <>
                <dt>Policy #</dt>
                <dd>{selectedRow.policyNumber}</dd>
              </>
            )}
            <dt>Carrier</dt>
            <dd>{selectedRow.carrier}</dd>
            {selectedRow.producer && (
              <>
                <dt>Producer</dt>
                <dd>{selectedRow.producer}</dd>
              </>
            )}
            <dt>Quoted on</dt>
            <dd>{selectedRow.date}</dd>
          </dl>
        </div>
      )}

    
      {!isProspect && selectedRow.notes && (
        <div className="rql-details__notes">
          <div className="rql-details__notes-label">
            <FiFileText size={11} />
            Notes
          </div>
          <p>{selectedRow.notes}</p>
        </div>
      )}

    
      {opportunities !== null && (
        <div className="rql-details__section rql-opp-section">
          <div className="rql-details__section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiTrendingUp size={11} />
            Opportunities
            {opportunities.length > 0 && (
              <span className="rql-opp-count">{opportunities.length}</span>
            )}
          </div>

          {opportunities.length === 0 ? (
            <div className="rql-opp-empty">
              <FiCalendar size={13} />
              No opportunities found for this prospect.
            </div>
          ) : (
            <div className="rql-opp-list">
              {opportunities.map((opp: OpportunityRecord, idx: number) => (
                <div key={opp.databaseId} className="rql-opp-card">
                  {/* Card header — number + LOB */}
                  <div className="rql-opp-card__header">
                    <span className="rql-opp-card__num">#{idx + 1}</span>
                    <span className="rql-opp-card__lob">{opp.lineOfBusinessName || '—'}</span>
                  </div>
                  {/* Detail grid */}
                  <dl className="rql-opp-card__grid">
                    <dt>Effective Date</dt>
                    <dd>{formatDate(opp.neededBy)}</dd>

                    <dt>Stage</dt>
                    <dd>
                      <span className="rql-opp-card__stage-inline">
                        <span className="rql-opp-card__stage-dot" />
                        {opp.opportunityStageName || 'Not Assigned'}
                      </span>
                    </dd>

                    <dt>Win Probability</dt>
                    <dd style={{ color: WIN_PROB_COLOR[opp.winProbability ?? ''] ?? 'var(--text-secondary)', fontWeight: 600 }}>
                      {opp.winProbability || '—'}
                    </dd>

                    <dt>Assigned To</dt>
                    <dd>{opp.assignedTo.length > 0 ? opp.assignedTo.join(', ') : '—'}</dd>

                    <dt>Type</dt>
                    <dd>{opp.createdFromRenewal ? 'Renewal' : 'New Business'}</dd>

                    {opp.description && (
                      <>
                        <dt>Description</dt>
                        <dd className="rql-opp-card__desc-cell">{opp.description}</dd>
                      </>
                    )}
                  </dl>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Opportunities skeleton — only for prospect (New tab) rows */}
      {opportunities === null && selectedRow && isProspect && (
        <SkeletonOpportunities />
      )}

      {policyPickerOpen && (
        <PolicyTypePicker
          onSelect={handleExecute}
          onClose={() => setPolicyPickerOpen(false)}
        />
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   WORKFLOW VIEW
   ══════════════════════════════════════════════════════════════════════════ */

const STEP_ICONS: Partial<Record<StepStatus, React.ComponentType<{ size?: number }>>> = {
  running:        FiRefreshCw,
  awaiting_input: FiHelpCircle,
  completed:      FiCheck,
  failed:         FiAlertTriangle,
};

const STEP_PILL_LABEL: Record<StepStatus, string> = {
  pending:        'Pending',
  running:        'In Progress',
  awaiting_input: 'Needs input',
  completed:      'Completed',
  failed:         'Failed',
};

/* Live sub-step trail rendered under each step. The most recent `running`
   item gets a pulsing icon (the "thinking" indicator). */
const SubProgress: React.FC<{ items: ProgressItem[] }> = ({ items }) => {
  if (!items?.length) return null;
  // Deduplicate by key — keep first occurrence (that's the one the backend
  // updates via set_progress_item_status, which breaks on first match).
  const seen = new Set<string>();
  const deduped = items.filter(it => {
    if (seen.has(it.key)) return false;
    seen.add(it.key);
    return true;
  });
  return (
    <ul className="rql-substep">
      {deduped.map((it, i) => {
        const isLast = i === deduped.length - 1;
        return (
          <li key={`${it.key}-${i}`} className={`rql-substep__item rql-substep__item--${it.status}`}>
            <span className="rql-substep__icon" aria-hidden="true">
              {it.status === 'running'   && <FiRefreshCw size={9} className="rql-substep__spin" />}
              {it.status === 'completed' && <FiCheck size={9} />}
              {it.status === 'failed'    && <FiAlertTriangle size={9} />}
            </span>
            <span className="rql-substep__text">
              <span className="rql-substep__label">{it.label}</span>
              {it.detail && <span className="rql-substep__detail">{it.detail}</span>}
            </span>
            {!isLast && <span className="rql-substep__line" />}
          </li>
        );
      })}
    </ul>
  );
};

/* Inline pause-for-input UI. Three modes:
   • Review (when prompt.review is set): live review card + full AI chat
     interface with coverage overrides, suggestions, and approval banner —
     exactly matching the InlineWorkflowPanel experience.
   • Chat (when prompt.fields is empty): chat bubble + single freeform reply.
   • Form (when prompt.fields is non-empty): chat bubble + structured inputs.
   All share the same "agent bubble" treatment so it feels conversational. */
const InlineInputPrompt: React.FC<{
  runId: string;
  stepId: string;
  prompt: AwaitingInputPrompt;
}> = ({ runId, stepId, prompt }) => {
  const { submitStepInput, getBackendId } = useRateQuoteLink();
  const isReview = !!prompt.review;
  const isChat = !isReview && (!prompt.fields || prompt.fields.length === 0);
  const backendRunId = getBackendId(runId);
  // DOM ref used to read actual field values via FormData on submit,
  // bypassing any React state/closure sync issues entirely.
  const formRef = useRef<HTMLFormElement>(null);

  // ── Review-mode chat state ─────────────────────────────────────────────────
  const runChat = useRunChat(backendRunId);
  const [chatInput, setChatInput] = useState('');
  const [reviewOverride, setReviewOverride] = useState<AwaitingReviewPayload | null>(null);
  const reviewOverridesRef = useRef<AwaitingReviewPayload | null>(null);
  const [aiSuggestsConfirm, setAiSuggestsConfirm] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<Array<{ key: string; current: string; recommended: string; reason: string }>>([]);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  const seededRef = useRef(false);

  // Seed the initial message once:
  // • Review gate  → generic review prompt (shown in the chat thread above ReviewCard)
  // • Field-input  → the AI question text (so _persist includes it in MongoDB history)
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (isReview) {
      runChat.seed('Review the details below. Ask me to change coverages, drivers, carriers, or effective date before submitting.');
    } else if (!isChat && prompt.prompt) {
      // Append the AI question so _persist (called by localTransact on submit)
      // saves it to MongoDB alongside the user's answer and "Got it" reply.
      runChat.append(prompt.prompt);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [runChat.messages, runChat.thinking]);

  // ── Non-review mode state ──────────────────────────────────────────────────
  /**
   * Unique state key for a field. Priority: key → name → label.
   * `label` is always unique per-prompt and makes submissions readable by the agent.
   */
  const fk = (f: (typeof prompt.fields)[number]): string =>
    f.key || f.name || f.label || '';

  // Pre-compute field keys once so every render pass uses the exact same key —
  // avoids closure capture issues where fk(f) could differ between onChange and value lookup.
  const fieldKeys = prompt.fields.map((f, i) => fk(f) || String(i));

  const [values, setValues] = useState<Record<string, string>>(() =>
    isReview
      ? { confirmed: 'yes' }
      : isChat
        ? { reply: '' }
        : Object.fromEntries(fieldKeys.map(k => [k, ''])),
  );
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const onChange = (name: string, v: string) => setValues(s => ({ ...s, [name]: v }));

  const missingRequired = isReview
    ? false
    : isChat
      ? !(values.reply ?? '').trim()
      : prompt.fields.some((f, i) => f.required && !(values[fieldKeys[i]] ?? '').trim());

  /** Check if a field is a date-type field based on name/label. */
  const _isDateField = (f: typeof prompt.fields[number]) => {
    const n = (f.label ?? '').toLowerCase();
    return n.includes('date') || n.includes('dob') || n.includes('birth');
  };
  /** Validate MM/DD/YYYY format. */
  const _validateDate = (val: string): string | null => {
    if (!val.trim()) return null;
    const m = val.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
    if (!m) return 'Please enter a valid date in MM/DD/YYYY format.';
    const [, mm, dd, yyyy] = m;
    const month = parseInt(mm, 10), day = parseInt(dd, 10), year = parseInt(yyyy, 10);
    if (month < 1 || month > 12) return 'Month must be between 01 and 12.';
    if (day < 1 || day > 31) return 'Day must be between 01 and 31.';
    if (year < 1900 || year > 2100) return 'Year must be between 1900 and 2100.';
    return null;
  };
  /** Auto-format date: insert slashes as user types digits. */
  const _autoFmtDate = (raw: string, prev: string): string => {
    const digits = raw.replace(/[^\d]/g, '');
    if (raw.length < prev.length) return raw;
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return digits.slice(0, 2) + '/' + digits.slice(2);
    return digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4, 8);
  };
  const hasDateErrors = !isReview && !isChat && prompt.fields.some((f, i) => _isDateField(f) && (values[fieldKeys[i]] ?? '').trim() && _validateDate(values[fieldKeys[i]] ?? ''));

  const sendChat = useCallback(async (text: string) => {
    if (!text.trim()) return;
    setChatInput('');
    const data = await runChat.send(text);
    if (!data) return;
    if (data.suggestions && Array.isArray(data.suggestions) && data.suggestions.length > 0 && runChat.messages.length <= 2) {
      setAiSuggestions(data.suggestions);
    }
    if (data.updated_review) {
      setReviewOverride(data.updated_review);
      reviewOverridesRef.current = data.updated_review;
    }
    if (data.action === 'confirm') setAiSuggestsConfirm(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runChat.send, runChat.messages.length]);

  /**
   * Read the actual field values from the DOM via FormData.
   * This is the source of truth for submission — bypasses any React state
   * sync/closure issues that can cause keys like "undefined" to appear.
   */
  const getFormValues = (): Record<string, string> => {
    if (!formRef.current || isReview || isChat) return { ...values };
    const fd = new FormData(formRef.current);
    const result: Record<string, string> = {};
    fieldKeys.forEach(k => {
      result[k] = (fd.get(k) as string) ?? '';
    });
    return result;
  };

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (submitting || hasDateErrors) return;

    // Read actual DOM values — reliable regardless of React state
    const rawValues = isReview || isChat ? { ...values } : getFormValues();

    // Runtime required-field check against actual DOM values
    const anyMissing = !isReview && !isChat &&
      prompt.fields.some((f, i) => f.required && !(rawValues[fieldKeys[i]] ?? '').trim());
    if (anyMissing) return;

    // Build readable display text for the chat bubble
    const userText = isReview
      ? 'Confirmed'
      : isChat
        ? (rawValues.reply ?? '').trim() || 'Submitted'
        : prompt.fields.length === 1
          ? (Object.values(rawValues)[0] ?? '').trim() || 'Submitted'
          : prompt.fields.map((f, i) => `${f.label}: ${rawValues[fieldKeys[i]] ?? ''}`)
              .filter(s => s.split(': ')[1]?.trim()).join(', ') || 'Submitted';

    const inputs: Record<string, any> = isReview ? { confirmed: 'yes' } : { ...rawValues };
    if (isReview && reviewOverridesRef.current) {
      inputs.review_overrides = reviewOverridesRef.current;
    }
    setSubmitting(true);
    try {
      await runChat.localTransact(
        userText,
        async () => { await submitStepInput(runId, stepId, inputs); },
        "Got it — continuing the workflow…",
        "Couldn't submit that — please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Review mode — full chat interface ─────────────────────────────────────
  if (isReview && prompt.review) {
    const currentReview = reviewOverride ?? prompt.review;
    return (
      <div className="rql-prompt rql-prompt--review">
        {/* Agent bubble */}
        <div className="rql-prompt__bubble">
          <span className="rql-prompt__avatar" aria-hidden="true">
            <RiSparklingFill size={11} />
          </span>
          <div className="rql-prompt__bubble-text">
            <div className="rql-prompt__question">{prompt.prompt}</div>
            {prompt.reason && <div className="rql-prompt__reason">{prompt.reason}</div>}
          </div>
        </div>

        {/* Live review card — updates in real time as AI applies patches */}
        <ReviewCard review={currentReview} />

        {/* Chat thread */}
        <div className="rql-prompt__chat-thread">
          {runChat.messages.map(m => (
            <div key={m.id} className={`rql-chat__msg rql-chat__msg--${m.role}`}>
              {m.role === 'ai' && (
                <span className="rql-chat__avatar"><RiSparklingFill size={11} /></span>
              )}
              <span className="rql-chat__bubble">
                {m.role === 'ai' ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p:          ({ children }) => <p className="rql-chat__md-p">{children}</p>,
                      ul:         ({ children }) => <ul className="rql-chat__md-ul">{children}</ul>,
                      ol:         ({ children }) => <ol className="rql-chat__md-ol">{children}</ol>,
                      li:         ({ children }) => <li className="rql-chat__md-li">{children}</li>,
                      strong:     ({ children }) => <strong className="rql-chat__md-strong">{children}</strong>,
                      em:         ({ children }) => <em className="rql-chat__md-em">{children}</em>,
                      h1:         ({ children }) => (
                        <div className="rql-chat__md-h1" style={{ fontSize: '12px', fontWeight: 700, margin: '12px 0 5px', paddingBottom: '4px', lineHeight: 1.3, letterSpacing: '0.03em' }}>{children}</div>
                      ),
                      h2:         ({ children }) => (
                        <div className="rql-chat__md-h2" style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '10px 0 4px', paddingBottom: '3px', lineHeight: 1.3 }}>{children}</div>
                      ),
                      h3:         ({ children }) => (
                        <div className="rql-chat__md-h3" style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '8px 0 3px' }}>{children}</div>
                      ),
                      hr:         () => <hr className="rql-chat__md-hr" />,
                      blockquote: ({ children }) => <blockquote className="rql-chat__md-blockquote">{children}</blockquote>,
                      table:      ({ children }) => <div className="rql-chat__md-table-wrap"><table className="rql-chat__md-table">{children}</table></div>,
                      thead:      ({ children }) => <thead className="rql-chat__md-thead">{children}</thead>,
                      tbody:      ({ children }) => <tbody className="rql-chat__md-tbody">{children}</tbody>,
                      tr:         ({ children }) => <tr className="rql-chat__md-tr">{children}</tr>,
                      th:         ({ children }) => <th className="rql-chat__md-th">{children}</th>,
                      td:         ({ children }) => <td className="rql-chat__md-td">{children}</td>,
                    }}
                  >
                    {m.text}
                  </ReactMarkdown>
                ) : m.text}
              </span>
            </div>
          ))}
          {runChat.thinking && (
            <div className="rql-chat__msg rql-chat__msg--ai">
              <span className="rql-chat__avatar"><RiSparklingFill size={11} /></span>
              <span className="rql-chat__bubble rql-chat__bubble--thinking">
                <span className="rql-chat__dot" />
                <span className="rql-chat__dot" />
                <span className="rql-chat__dot" />
              </span>
            </div>
          )}
          <div ref={chatBottomRef} />
        </div>

        {/* AI coverage suggestions (first reply only) */}
        {aiSuggestions.length > 0 && (
          <div className="rql-ai-suggestions">
            <div className="rql-ai-suggestions__head">
              <RiSparklingFill size={10} /> AI Coverage Insights
            </div>
            <div className="rql-ai-suggestions__list">
              {aiSuggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className="rql-ai-suggestions__item"
                  onClick={() => sendChat(`Change ${s.key} to ${s.recommended}`)}
                  title={s.reason}
                >
                  <span className="rql-ai-suggestions__field">{s.key.replace(/_/g, ' ')}</span>
                  <span className="rql-ai-suggestions__arrow">→</span>
                  <span className="rql-ai-suggestions__rec">{s.recommended}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* AI confirm banner — shown when AI detects submission intent */}
        {aiSuggestsConfirm && (
          <div className="rql-iwf__approval-banner">
            <div className="rql-iwf__approval-banner-head">
              <span className="rql-iwf__approval-banner-icon">
                <RiSparklingFill size={13} />
              </span>
              <span className="rql-iwf__approval-banner-title">AI is ready to submit</span>
            </div>
            <p className="rql-iwf__approval-hint">
              Review the details above and confirm to send to carriers, or keep chatting to make further adjustments.
            </p>
            <button
              type="button"
              className="rql-prompt__submit rql-prompt__submit--review"
              disabled={submitting}
              onClick={() => onSubmit()}
            >
              {submitting
                ? <><FiRefreshCw size={13} className="rql-row-execute__spin" /> Submitting…</>
                : <><FiCheck size={13} /> Confirm &amp; Submit to Carriers</>}
            </button>
          </div>
        )}

        {/* Quick suggestion chips */}
        <div className="rql-chat__suggestions">
          {SIDEBAR_CHAT_SUGGESTIONS.map(s => (
            <button key={s} type="button" className="rql-chat__suggestion" onClick={() => sendChat(s)}>
              {s}
            </button>
          ))}
        </div>

        {/* Chat input bar */}
        <div className="rql-chat__inputbar">
          <div className="rql-chat__inputwrap">
            <input
              className="rql-chat__input"
              placeholder="Ask AI to adjust coverages, carriers, drivers…"
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(chatInput); }
              }}
              disabled={runChat.thinking}
            />
          </div>
          <button
            type="button"
            className="rql-chat__send"
            disabled={!chatInput.trim() || runChat.thinking}
            onClick={() => sendChat(chatInput)}
          >
            <FiSend size={13} />
          </button>
        </div>

        {/* Always-visible submit button (hidden when approval banner is active) */}
        {!aiSuggestsConfirm && (
          <div className="rql-prompt__actions rql-prompt__actions--review">
            <button
              type="button"
              className="rql-prompt__submit rql-prompt__submit--review"
              disabled={submitting}
              onClick={() => onSubmit()}
            >
              {submitting
                ? <><FiRefreshCw size={13} className="rql-row-execute__spin" /> Submitting…</>
                : <><FiCheck size={13} /> Review &amp; Submit to Carriers</>}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Original non-review form ───────────────────────────────────────────────
  return (
    <form
      ref={formRef}
      className={`rql-prompt${isChat ? ' rql-prompt--chat' : ''}`}
      autoComplete="off"
      onSubmit={onSubmit}
    >
      {/* Agent bubble — the question + the WHY */}
      <div className="rql-prompt__bubble">
        <span className="rql-prompt__avatar" aria-hidden="true">
          <RiSparklingFill size={11} />
        </span>
        <div className="rql-prompt__bubble-text">
          <div className="rql-prompt__question">{prompt.prompt}</div>
          {prompt.reason && (
            <div className="rql-prompt__reason">{prompt.reason}</div>
          )}
        </div>
      </div>

      {/* Body — freeform reply OR structured form */}
      {isChat ? (
        <div className="rql-prompt__reply-row">
          <textarea
            className="rql-prompt__reply"
            value={values.reply ?? ''}
            placeholder="Type your reply…"
            rows={2}
            onChange={e => onChange('reply', e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e as unknown as React.FormEvent);
              }
            }}
            autoFocus
          />
        </div>
      ) : (
        <div className="rql-prompt__fields">
          {prompt.fields.map((f, i) => {
            const fkey = fieldKeys[i];
            const isDate = _isDateField(f);
            const val = values[fkey] ?? '';
            const dateErr = isDate && touched[fkey] && val.trim() ? _validateDate(val) : null;
            return (
              <label key={fkey} className="rql-prompt__field">
                <span className="rql-prompt__label">
                  {f.label}
                  {isDate && <span className="rql-prompt__format"> (MM/DD/YYYY)</span>}
                  {f.required && <span className="rql-prompt__req">*</span>}
                </span>
                {f.type === 'select' && f.options ? (
                  <select
                    className="rql-prompt__input"
                    name={fkey}
                    value={val}
                    onChange={e => onChange(fkey, e.target.value)}
                    required={f.required}
                  >
                    <option value="">Select…</option>
                    {f.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : (
                  <input
                    className={`rql-prompt__input${dateErr ? ' rql-prompt__input--error' : ''}`}
                    type="text"
                    inputMode={f.type === 'number' ? 'numeric' : undefined}
                    id={`rql-field-${fkey}`}
                    name={fkey}
                    autoComplete="off"
                    value={val}
                    placeholder={isDate ? 'MM/DD/YYYY' : (f.type === 'number' ? '0' : f.placeholder)}
                    maxLength={isDate ? 10 : undefined}
                    onChange={e => {
                      const raw = e.target.value;
                      const v = isDate
                        ? _autoFmtDate(raw, val)
                        : f.type === 'number'
                          ? raw.replace(/[^0-9.]/g, '')
                          : raw;
                      onChange(fkey, v);
                    }}
                    onBlur={() => setTouched(s => ({ ...s, [fkey]: true }))}
                    required={f.required}
                  />
                )}
                {dateErr && <span className="rql-prompt__field-error">{dateErr}</span>}
              </label>
            );
          })}
        </div>
      )}

      <div className="rql-prompt__actions">
        <button
          type="submit"
          className="rql-prompt__submit"
          disabled={missingRequired || hasDateErrors || submitting}
        >
          {submitting
            ? <><FiRefreshCw size={11} className="rql-row-execute__spin" /> Sending…</>
            : isChat
              ? <><FiSend size={11} /> Send</>
              : <><FiSend size={11} /> Submit &amp; continue</>}
        </button>
      </div>
    </form>
  );
};

/* Read-only review card — what's about to be sent to carriers.
   Pulls insured/state/effective + drivers/vehicles/carriers + coverages out of
   the workflow's review payload and renders them in compact sections. */
const ReviewCard: React.FC<{ review: AwaitingReviewPayload }> = ({ review }) => {
  // Build driver meta line lazily so we only show separators between fields
  // that actually have values.
  const driverMeta = (d: NonNullable<AwaitingReviewPayload['drivers']>[number]) => {
    const parts: string[] = [];
    if (d.dob)            parts.push(`DOB ${d.dob}`);
    if (d.license_number) parts.push(`Lic # ${d.license_number}`);
    if (d.license_state)  parts.push(`State ${d.license_state}`);
    return parts.join(' · ');
  };

  // Friendly coverage key labels — fall back to titlecasing the raw key so a
  // new field added on the backend still renders something readable.
  const COVERAGE_LABELS: Record<string, string> = {
    bodily_injury_per_person:    'BI per person',
    bodily_injury_per_accident:  'BI per accident',
    property_damage:             'Property damage',
    uninsured_motorist:          'Uninsured motorist',
    underinsured_motorist:       'Underinsured motorist',
    pip:                         'PIP',
    medical_payments:            'Medical payments',
    comprehensive_deductible:    'Comp deductible',
    collision_deductible:        'Collision deductible',
    rental_reimbursement:        'Rental reimbursement',
    towing:                      'Towing',
  };
  const titlecase = (s: string) => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="rql-review">
      <div className="rql-review__grid">
        <div className="rql-review__cell">
          <span className="rql-review__cell-label">Insured</span>
          <span className="rql-review__cell-value">{review.insured_name || '—'}</span>
        </div>
        <div className="rql-review__cell">
          <span className="rql-review__cell-label">State</span>
          <span className="rql-review__cell-value">{review.state || '—'}</span>
        </div>
        <div className="rql-review__cell">
          <span className="rql-review__cell-label">Effective date</span>
          <span className="rql-review__cell-value">{review.effective_date || '—'}</span>
        </div>
        <div className="rql-review__cell">
          <span className="rql-review__cell-label">Policy type</span>
          <span className="rql-review__cell-value">{review.policy_type || '—'}</span>
        </div>
        {review.application_id && (
          <div className="rql-review__cell rql-review__cell--wide">
            <span className="rql-review__cell-label">Application ID</span>
            <span className="rql-review__cell-value">{review.application_id}</span>
          </div>
        )}
      </div>

      {review.drivers && review.drivers.length > 0 && (
        <div className="rql-review__section">
          <div className="rql-review__section-head">
            <FiUser size={10} />
            <span>Drivers ({review.drivers.length})</span>
          </div>
          <ul className="rql-review__list">
            {review.drivers.map((d, i) => {
              const meta = driverMeta(d);
              return (
                <li key={i}>
                  <span className="rql-review__list-name">{d.name || `Driver ${i + 1}`}</span>
                  {meta && <span className="rql-review__list-meta">{meta}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {review.vehicles && review.vehicles.length > 0 && (
        <div className="rql-review__section">
          <div className="rql-review__section-head">
            <FiFileText size={10} />
            <span>Vehicles ({review.vehicles.length})</span>
          </div>
          <ul className="rql-review__list">
            {review.vehicles.map((v, i) => {
              const meta: string[] = [];
              if (v.vin)   meta.push(`VIN ${v.vin}`);
              if (v.usage) meta.push(`Usage ${v.usage}`);
              return (
                <li key={i}>
                  <span className="rql-review__list-name">
                    {[v.year, v.make, v.model].filter(Boolean).join(' ') || `Vehicle ${i + 1}`}
                  </span>
                  {meta.length > 0 && (
                    <span className="rql-review__list-meta">{meta.join(' · ')}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {review.coverages && review.coverages.length > 0 && (
        <div className="rql-review__section">
          <div className="rql-review__section-head">
            <FiCheck size={10} />
            <span>Coverages ({review.coverages.length})</span>
          </div>
          <dl className="rql-review__kv">
            {review.coverages.map((c) => (
              <React.Fragment key={c.key}>
                <dt>{COVERAGE_LABELS[c.key] || titlecase(c.key)}</dt>
                <dd>{c.value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      )}

      {review.carriers && review.carriers.length > 0 && (
        <div className="rql-review__section">
          <div className="rql-review__section-head">
            <FiCheck size={10} />
            <span>Sending to ({review.carriers.length})</span>
          </div>
          <ul className="rql-review__chips">
            {review.carriers.map((c, i) => (
              <li key={i} className="rql-review__chip">
                {c.name || `Carrier ${c.provider_id || i + 1}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const StepRow: React.FC<{
  step: WorkflowStep;
  index: number;
  total: number;
  runId: string;
}> = ({ step, index, total, runId }) => {
  const Icon = STEP_ICONS[step.status];
  const isLast = index === total - 1;

  return (
    <div className={`rql-step rql-step--${step.status}`}>
      <div className="rql-step__rail">
        <span className={`rql-step__dot rql-step__dot--${step.status}`}>
          {Icon ? <Icon size={11} /> : <span className="rql-step__dot-num">{index + 1}</span>}
        </span>
        {!isLast && <span className={`rql-step__line rql-step__line--${step.status}`} />}
      </div>
      <div className="rql-step__body">
        <div className="rql-step__head">
          <div className="rql-step__head-left">
            <span className="rql-step__num">Step {index + 1}</span>
            <span className="rql-step__label">{step.label}</span>
          </div>
          <span className={`rql-step__pill rql-step__pill--${step.status}`}>
            {STEP_PILL_LABEL[step.status]}
          </span>
        </div>
        {step.description && <div className="rql-step__desc">{step.description}</div>}

        {/* Live sub-step trail (sub-actions inside this step) */}
        {step.progress && step.progress.length > 0 && (
          <SubProgress items={step.progress} />
        )}

        {/* Inline chatbot-like input form when paused for user input */}
        {step.status === 'awaiting_input' && step.awaitingInput && (
          <InlineInputPrompt
            key={step.awaitingInput.prompt ?? step.id}
            runId={runId}
            stepId={step.id}
            prompt={step.awaitingInput}
          />
        )}

        {step.durationMs ? <div className="rql-step__meta">{formatDuration(step.durationMs)}</div> : null}
      </div>
    </div>
  );
};

const WorkflowCard: React.FC<{ run: RunEntry; isRunning: boolean }> = ({ run, isRunning }) => {
  const meta = STATUS_META[run.status];
  const completed = run.steps.filter(s => s.status === 'completed').length;
  const total = run.steps.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isPaused = run.status === 'awaiting_input'
    || run.steps.some(s => s.status === 'awaiting_input');
  const isActive = isRunning || isPaused;
  const cardMod = isPaused ? 'paused' : isRunning ? 'running' : run.status === 'failed' ? 'failed' : 'done';

  // Auto-expand whenever the run is in flight OR paused waiting for input
  // (don't make the user click "View steps" to find the prompt).
  const [expanded, setExpanded] = useState<boolean>(isActive);
  useEffect(() => { if (isActive) setExpanded(true); }, [isActive]);

  const timestamp = run.completedAt ?? run.startedAt;

  const title = isPaused
    ? 'Workflow paused — needs input'
    : isRunning
      ? 'Workflow in progress'
      : run.status === 'completed' ? 'Workflow complete' : 'Workflow failed';

  return (
    <div className={`rql-workflow rql-workflow--${cardMod}`}>
      {isPaused && (
        <div className="rql-workflow__paused-banner">
          <FiHelpCircle size={12} />
          <span>Workflow paused — your input is required to continue</span>
        </div>
      )}
      <div className="rql-workflow__head">
        <span
          className={`rql-workflow__status${isRunning ? ' rql-workflow__status--running' : ''}${isPaused ? ' rql-workflow__status--awaiting' : ''}`}
          style={{ background: meta.bg, color: meta.color, borderColor: meta.border }}
        >
          {isRunning
            ? <FiRefreshCw size={14} className="rql-workflow__status-spin" />
            : isPaused
              ? <FiHelpCircle size={14} />
              : <meta.Icon size={14} />}
        </span>
        <div className="rql-workflow__head-text">
          <div className="rql-workflow__title">{title}</div>
          <div className="rql-workflow__sub" title={run.label}>
            {run.workflow === 'renewal' ? 'Renewal' : 'New business'} · {run.label}
          </div>
        </div>
        <span className="rql-workflow__count">{completed}/{total}</span>
      </div>

      <div className="rql-workflow__bar">
        <motion.div
          className="rql-workflow__bar-fill"
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.35 }}
        />
      </div>

      <div className="rql-workflow__meta-row">
        <TriggerBadge trigger={run.trigger} size="sm" />
        <span className="rql-workflow__meta-time">
          <FiClock size={10} />
          {formatRelative(timestamp)}
          {run.durationMs ? (
            <>
              <span className="rql-run-hero__dot-sep" />
              {formatDuration(run.durationMs)}
            </>
          ) : null}
        </span>
      </div>

      <div className="rql-workflow__strip">
        {run.steps.map((s, i) => (
          <span
            key={s.id}
            className={`rql-workflow__strip-dot rql-workflow__strip-dot--${s.status}`}
            title={`${i + 1}. ${s.label} · ${STEP_PILL_LABEL[s.status]}`}
          />
        ))}
      </div>

      <button
        type="button"
        className={`rql-workflow__toggle${expanded ? ' rql-workflow__toggle--open' : ''}`}
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <span>{expanded ? 'Hide steps' : `View ${total} steps`}</span>
        <FiChevronDown size={12} className="rql-workflow__toggle-chev" />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="steps"
            className="rql-workflow__steps"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22 }}
          >
            {run.steps.map((step, idx) => (
              <StepRow key={step.id} step={step} index={idx} total={run.steps.length} runId={run.id} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Carrier quote results (after the final step completes) */}
      {run.quotes && run.quotes.length > 0 && <QuoteResults quotes={run.quotes} />}

      <div className="rql-workflow__attr">
        <span className="rql-workflow__attr-label">Executed by</span>
        <span className="rql-workflow__attr-value">{run.executedBy}</span>
        <span className="rql-workflow__attr-sep" />
        <span className="rql-workflow__attr-label">via</span>
        <span className="rql-workflow__attr-value">{run.triggerSource}</span>
      </div>
    </div>
  );
};

/* ── Carrier quote results ─────────────────────────────────────────────── */

function formatPremium(v?: number | null): string | null {
  if (v == null) return null;
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const QuoteResults: React.FC<{ quotes: QuoteResult[] }> = ({ quotes }) => {
  const quoted = quotes.filter(q => (q.statusCode || q.quoteType) === 'QUOTED');
  const best = quoted.reduce<QuoteResult | null>((b, q) => {
    const p = q.premiumAmount ?? q.totalAmount;
    if (p == null) return b;
    const bp = b ? (b.premiumAmount ?? b.totalAmount) : null;
    return bp == null || p < bp! ? q : b;
  }, null);

  return (
    <div className="rql-quotes">
      <div className="rql-quotes__summary">
        <span>{quoted.length} of {quotes.length} quoted</span>
        {best && formatPremium(best.premiumAmount ?? best.totalAmount) && (
          <span className="rql-quotes__best">
            Best {formatPremium(best.premiumAmount ?? best.totalAmount)} · {best.carrierName}
          </span>
        )}
      </div>

      {quotes.map((q, i) => {
        const failed = (q.statusCode || q.quoteType) === 'FAILED';
        const premium = formatPremium(q.premiumAmount ?? q.totalAmount);
        return (
          <div key={q.quoteId || i} className={`rql-quote${failed ? ' rql-quote--failed' : ''}`}>
            <div className="rql-quote__head">
              {q.carrierLogoURL
                ? <img className="rql-quote__logo" src={q.carrierLogoURL} alt="" />
                : <span className="rql-quote__logo rql-quote__logo--ph">{(q.carrierName || '?')[0]}</span>}
              <span className="rql-quote__carrier">{q.carrierName || q.carrierId}</span>
              <span className={`rql-quote__status rql-quote__status--${failed ? 'failed' : 'quoted'}`}>
                {failed ? <FiAlertTriangle size={10} /> : <FiCheck size={10} />}
                {failed ? 'Failed' : 'Quoted'}
              </span>
            </div>

            {!failed && premium && (
              <div className="rql-quote__premium-row">
                <span className="rql-quote__premium">{premium}</span>
                <span className="rql-quote__premium-label">/ term</span>
                {q.carrierBridgingLink && (
                  <a className="rql-quote__bind" href={q.carrierBridgingLink} target="_blank" rel="noopener noreferrer">
                    Bind →
                  </a>
                )}
              </div>
            )}

            {failed && q.failureReason && (
              <div className="rql-quote__failure" title={q.failureReason}>{q.failureReason}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const WorkflowView: React.FC = () => {
  const { activeRun, lastRun, isRunning } = useRateQuoteLink();
  const featuredRun = activeRun ?? lastRun;

  if (!featuredRun) {
    return (
      <div className="rql-workflow-empty">
        <div className="rql-workflow-empty__icon">
          <RiSparklingFill size={18} />
        </div>
        <div className="rql-workflow-empty__title">No workflow yet</div>
        <div className="rql-workflow-empty__hint">
          Click <strong>Execute</strong> on any row to start an AI-powered auto-quote workflow.
        </div>
      </div>
    );
  }

  return <WorkflowCard run={featuredRun} isRunning={isRunning} />;
};

/* ══════════════════════════════════════════════════════════════════════════
   HISTORY VIEW (merged with execution insights)
   ══════════════════════════════════════════════════════════════════════════ */

const EVENT_TYPE_LABELS: Record<string, string> = {
  run_started:       'Run Started',
  run_completed:     'Run Completed',
  run_failed:        'Run Failed',
  step_started:      'Step Started',
  step_completed:    'Step Completed',
  step_failed:       'Step Failed',
  carrier_submitted: 'Carrier Submitted',
  quote_received:    'Quote Received',
  proposal_created:  'Proposal Created',
  email_drafted:     'Email Drafted',
  email_sent:        'Email Sent',
  review_started:    'Review Started',
  review_confirmed:  'Review Confirmed',
  chat_message:      'Chat Message',
};

const HistoryView: React.FC = () => {
  const { history, historyRuns, settings, activeRun, runEvents, getBackendId } = useRateQuoteLink();
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  // Prefer real persisted runs from the backend; fall back to in-session runs.
  const runs = historyRuns ?? history;

  const total       = runs.length;
  const aiRuns      = runs.filter(h => h.trigger === 'ai').length;
  const manualRuns  = runs.filter(h => h.trigger === 'manual').length;
  const successRuns = runs.filter(h => h.status === 'completed').length;
  const successRate = total > 0 ? Math.round((successRuns / total) * 100) : 0;
  const aiShare     = total > 0 ? Math.round((aiRuns / total) * 100) : 0;

  return (
    <div className="rql-info">

      {/* Insights — stat tiles */}
      <div className="rql-info__stats">
        <div className="rql-info__stat">
          <span className="rql-info__stat-label">Total runs</span>
          <span className="rql-info__stat-value">{total}</span>
        </div>
        <div className="rql-info__stat rql-info__stat--ai">
          <span className="rql-info__stat-label">
            <RiSparklingFill size={10} />
            AI executed
          </span>
          <span className="rql-info__stat-value">{aiRuns}</span>
          <span className="rql-info__stat-pct">{aiShare}%</span>
        </div>
        <div className="rql-info__stat rql-info__stat--manual">
          <span className="rql-info__stat-label">
            <FiUser size={10} />
            Manual
          </span>
          <span className="rql-info__stat-value">{manualRuns}</span>
          <span className="rql-info__stat-pct">{100 - aiShare}%</span>
        </div>
        <div className="rql-info__stat rql-info__stat--success">
          <span className="rql-info__stat-label">
            <FiTrendingUp size={10} />
            Success
          </span>
          <span className="rql-info__stat-value">{successRate}%</span>
        </div>
      </div>

      {total > 0 && (
        <div className="rql-info__split">
          <div className="rql-info__split-label">
            <span>Execution mix</span>
            <span>{aiShare}% AI · {100 - aiShare}% Manual</span>
          </div>
          <div className="rql-info__split-bar">
            <div className="rql-info__split-ai" style={{ width: `${aiShare}%` }} />
            <div className="rql-info__split-manual" style={{ width: `${100 - aiShare}%` }} />
          </div>
        </div>
      )}

      {/* Active automation block */}
      <div className="rql-info__rules">
        <div className="rql-info__rules-title">Active automation</div>
        <ul className="rql-info__rules-list">
          <li>
            <FiCheck size={11} />
            Renewals fire <strong>{settings.renewal.daysBeforeExpiry} days</strong> before expiry
          </li>
          <li>
            <FiCheck size={11} />
            Daily sweep at <strong>{settings.renewal.autoRunTime}</strong>
          </li>
          <li>
            {settings.newLead.aiAutoExecute ? <FiCheck size={11} /> : <FiAlertTriangle size={11} />}
            AI auto-execute for new lead: <strong>{settings.newLead.aiAutoExecute ? 'On' : 'Off'}</strong>
          </li>
        </ul>
      </div>

      {/* Section divider */}
      <div className="rql-run-divider">
        <span>Execution history</span>
      </div>

      {/* Full execution timeline */}
      {runs.length === 0 ? (
        <div className="rql-empty" style={{ padding: '24px 8px' }}>
          No runs yet — click Execute on a row to start.
        </div>
      ) : (
        <div className="rql-timeline">
          {runs.map((run, idx) => {
            const meta = STATUS_META[run.status];
            const ts = run.completedAt ?? run.startedAt;
            return (
              <div key={run.id} className={`rql-timeline__row rql-timeline__row--${run.trigger}`}>
                <div className="rql-timeline__rail">
                  <span
                    className={`rql-timeline__dot${run.status === 'running' ? ' rql-timeline__dot--pulse' : ''}`}
                    style={{ background: meta.color, boxShadow: `0 0 0 3px ${meta.bg}` }}
                  />
                  {idx < runs.length - 1 && <span className="rql-timeline__line" />}
                </div>

                <div className="rql-timeline__content">
                  <div className="rql-timeline__row-top">
                    <span className="rql-timeline__label">{run.label}</span>
                    <span
                      className="rql-timeline__badge"
                      style={{ color: meta.color, borderColor: meta.border, background: meta.bg }}
                    >
                      <meta.Icon size={10} />
                      {meta.label}
                    </span>
                  </div>

                  <div className="rql-timeline__attribution">
                    <TriggerBadge trigger={run.trigger} size="sm" />
                    <span className="rql-timeline__source" title={run.triggerSource}>
                      {run.triggerSource}
                    </span>
                  </div>

                  <div className="rql-timeline__meta">
                    <FiClock size={10} />
                    <span>{formatRelative(ts)}</span>
                    {run.durationMs ? (
                      <>
                        <span className="rql-run-hero__dot-sep" />
                        <span>{formatDuration(run.durationMs)}</span>
                      </>
                    ) : null}
                    <span className="rql-run-hero__dot-sep" />
                    <span className="rql-timeline__executor">{run.executedBy}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Activity event log for the active run */}
      {(() => {
        if (!activeRun) return null;
        const backendId = getBackendId(activeRun.id);
        const allEvents: EventLogEntry[] = (backendId && runEvents[backendId]) || [];
        if (allEvents.length === 0) return null;
        const eventTypes = Array.from(new Set(allEvents.map(e => e.event_type)));
        const filtered = eventTypeFilter === 'all' ? allEvents : allEvents.filter(e => e.event_type === eventTypeFilter);
        return (
          <>
            <div className="rql-run-divider"><span>Activity log</span></div>
            <div className="rql-info__event-filter">
              <select
                className="rql-info__event-select"
                value={eventTypeFilter}
                onChange={ev => setEventTypeFilter(ev.target.value)}
              >
                <option value="all">All events</option>
                {eventTypes.map(t => (
                  <option key={t} value={t}>{EVENT_TYPE_LABELS[t] ?? t}</option>
                ))}
              </select>
            </div>
            <div className="rql-event-log">
              {filtered.map(ev => (
                <div key={ev.id} className="rql-event-log__row">
                  <div className="rql-event-log__dot" />
                  <div className="rql-event-log__body">
                    <span className="rql-event-log__type">
                      {EVENT_TYPE_LABELS[ev.event_type] ?? ev.event_type}
                    </span>
                    <span className="rql-event-log__actor">{ev.actor}</span>
                    <span className="rql-event-log__time">
                      {formatRelative(new Date(ev.created_at))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      })()}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   SETTINGS VIEW (inlined — replaces the old modal)
   ══════════════════════════════════════════════════════════════════════════ */

const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}> = ({ checked, onChange, label, hint }) => (
  <label className="rql-settings__toggle">
    <div className="rql-settings__toggle-text">
      <span className="rql-settings__toggle-label">{label}</span>
      {hint && <span className="rql-settings__toggle-hint">{hint}</span>}
    </div>
    <span className={`rql-settings__switch${checked ? ' rql-settings__switch--on' : ''}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="rql-settings__switch-thumb" />
    </span>
  </label>
);

const SettingsViewSkeleton: React.FC = () => (
  <div className="rql-settings-inline">
    <div className="rql-settings__hdr rql-settings__hdr--skel">
      <span className="rql-skel rql-skel--icon" />
      <div className="rql-settings__hdr-text">
        <span className="rql-skel rql-skel--md" style={{ height: 13 }} />
        <span className="rql-skel rql-skel--lg" style={{ height: 11, marginTop: 4 }} />
      </div>
    </div>
    {[0, 1].map(ci => (
      <div key={ci} className="rql-settings__card">
        <div className="rql-settings__card-head">
          <span className="rql-skel" style={{ width: 24, height: 24, borderRadius: 7, flexShrink: 0 }} />
          <span className="rql-skel rql-skel--sm" style={{ height: 12, flex: 1 }} />
          <span className="rql-skel" style={{ width: 58, height: 18, borderRadius: 100 }} />
        </div>
        <div className="rql-settings__card-body">
          {[0, 1, 2].map(ri => (
            <div key={ri} className="rql-settings__toggle" style={{ pointerEvents: 'none' }}>
              <div className="rql-settings__toggle-text">
                <span className="rql-skel rql-skel--sm" style={{ height: 12 }} />
                <span className="rql-skel rql-skel--lg" style={{ height: 10, marginTop: 4 }} />
              </div>
              <span className="rql-skel" style={{ width: 36, height: 20, borderRadius: 100, flexShrink: 0 }} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const SettingsView: React.FC = () => {
  const { settings, settingsLoading, updateSettings, refreshSettings, setPanelView } = useRateQuoteLink();
  const [draft, setDraft] = useState<RateQuoteLinkSettings>(settings);
  const [savedFlash, setSavedFlash] = useState(false);
  const [voiceAgents, setVoiceAgents] = useState<DfVoiceAgent[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<DfPhoneNumber[]>([]);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [agentDropOpen, setAgentDropOpen] = useState(false);

  // On every open, re-read from the API so the toggles reflect any isActive
  // changes made on the Momentum Routine page (pause/resume from routine mgmt).
  useEffect(() => { refreshSettings(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep draft in sync whenever the context settings update (e.g. after refreshSettings resolves).
  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  // Load voice agents + phone numbers when voice section is enabled.
  useEffect(() => {
    if (!draft.voice?.enabled || voiceAgents.length > 0) return;
    setVoiceLoading(true);
    Promise.all([getVoiceAgentsDF(), getPhoneNumbersDF()])
      .then(([agents, numbers]) => {
        setVoiceAgents(agents);
        setPhoneNumbers(numbers);
      })
      .catch(() => {/* silent — agents list stays empty */})
      .finally(() => setVoiceLoading(false));
  }, [draft.voice?.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also load on initial render if already enabled (resuming saved state).
  useEffect(() => {
    if (!settings.voice?.enabled) return;
    setVoiceLoading(true);
    Promise.all([getVoiceAgentsDF(), getPhoneNumbersDF()])
      .then(([agents, numbers]) => { setVoiceAgents(agents); setPhoneNumbers(numbers); })
      .catch(() => {})
      .finally(() => setVoiceLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [consent, setConsent] = useState(false);
  const [newLeadTagInput, setNewLeadTagInput] = useState('');
  const [renewalTagInput, setRenewalTagInput] = useState('');

  /** Parse input string into individual tags — supports comma, semicolon, and Enter */
  const parseTags = (raw: string): string[] =>
    raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);

  const addTag = (section: 'newLead' | 'renewal', value: string) => {
    const newTags = parseTags(value);
    if (!newTags.length) return;
    setDraft(d => {
      const existing = d[section].tags ?? [];
      const lowerSet = new Set(existing.map(t => t.toLowerCase()));
      const unique = newTags.filter(t => !lowerSet.has(t.toLowerCase()));
      if (!unique.length) return d;
      return { ...d, [section]: { ...d[section], tags: [...existing, ...unique] } };
    });
    if (section === 'newLead') setNewLeadTagInput('');
    else setRenewalTagInput('');
  };

  const removeTag = (section: 'newLead' | 'renewal', idx: number) => {
    setDraft(d => {
      const tags = [...(d[section].tags ?? [])];
      tags.splice(idx, 1);
      return { ...d, [section]: { ...d[section], tags } };
    });
  };

  /** Flush any pending tag text into draft and return the final settings object */
  const buildFinalDraft = (): typeof draft => {
    let final = { ...draft };
    const nlTags = parseTags(newLeadTagInput);
    if (nlTags.length) {
      const existing = final.newLead.tags ?? [];
      const lowerSet = new Set(existing.map(t => t.toLowerCase()));
      const unique = nlTags.filter(t => !lowerSet.has(t.toLowerCase()));
      if (unique.length) final = { ...final, newLead: { ...final.newLead, tags: [...existing, ...unique] } };
    }
    const rnTags = parseTags(renewalTagInput);
    if (rnTags.length) {
      const existing = final.renewal.tags ?? [];
      const lowerSet = new Set(existing.map(t => t.toLowerCase()));
      const unique = rnTags.filter(t => !lowerSet.has(t.toLowerCase()));
      if (unique.length) final = { ...final, renewal: { ...final.renewal, tags: [...existing, ...unique] } };
    }
    return final;
  };

  const dirty =
    JSON.stringify(draft) !== JSON.stringify(settings);

  // Tags are optional — when set, only matching prospects are triggered; when empty, all are triggered
  const newLeadHasTag = (draft.newLead.tags?.length ?? 0) > 0 || newLeadTagInput.trim().length > 0;
  const renewalHasTag = (draft.renewal.tags?.length ?? 0) > 0 || renewalTagInput.trim().length > 0;

  // Consent is required when any automation toggle is being turned ON
  const hasAnyEnabled = !!(draft.newLead.enabled || draft.renewal.enabled || draft.voice?.enabled);
  const wasAnyEnabled = !!(settings.newLead.enabled || settings.renewal.enabled || settings.voice?.enabled);
  const needsConsent = hasAnyEnabled && dirty;

  // Reset consent when draft changes
  useEffect(() => { setConsent(false); }, [draft.newLead.enabled, draft.renewal.enabled, draft.voice?.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = () => {
    const final = buildFinalDraft();
    setDraft(final);
    setNewLeadTagInput('');
    setRenewalTagInput('');
    updateSettings(final);
    setSavedFlash(true);
    setConsent(false);
    window.setTimeout(() => setSavedFlash(false), 1500);
  };

  if (settingsLoading) return <SettingsViewSkeleton />;

  return (
    <div className="rql-settings-inline">

      {/* ═══ BOX 1 — Quoting Automation ═══ */}
      <div className="rql-settings__box">
        <div className="rql-settings__box-header">
          <span className="rql-settings__box-header-icon"><FiZap size={13} /></span>
          <div className="rql-settings__box-header-title">Quoting Automation</div>
        </div>

        {/* New Lead row */}
        <div className="rql-settings__box-row">
          <span className="rql-settings__box-row-icon rql-settings__box-row-icon--new"><FiPlus size={11} /></span>
          <div className="rql-settings__box-row-text">
            <span className="rql-settings__box-row-label">New Lead</span>
            <span className="rql-settings__box-row-hint">
              {draft.newLead.enabled ? 'Checks every 3 hours' : 'Auto-quote new prospects'}
            </span>
          </div>
          <span
            className={`rql-settings__switch${draft.newLead.enabled ? ' rql-settings__switch--on' : ''}`}
            onClick={() => setDraft(d => ({ ...d, newLead: { ...d.newLead, enabled: !d.newLead.enabled, aiAutoExecute: !d.newLead.enabled } }))}
          >
            <input type="checkbox" checked={draft.newLead.enabled} onChange={() => {}} style={{ pointerEvents: 'none' }} />
            <span className="rql-settings__switch-thumb" />
          </span>
        </div>

        {/* New Lead — optional tag filter */}
        {draft.newLead.enabled && (
          <div className="rql-settings__box-expand">
            <div className="rql-settings__tag-section">
              <div className="rql-settings__tag-label">
                <FiFilter size={11} />
                <span>Filter by NowCerts Tags</span>
                <span className="rql-settings__tag-badge">Recommended</span>
              </div>
              <span className="rql-settings__tag-desc">
                Add tags to control which prospects are auto-quoted. Tags must match the prospect's tags in NowCerts AMS.
              </span>
              <div className="rql-settings__tag-box">
                {(draft.newLead.tags ?? []).map((tag, i) => (
                  <span key={i} className="rql-settings__tag-chip">
                    {tag}
                    <button type="button" className="rql-settings__tag-chip-x" onClick={() => removeTag('newLead', i)}>
                      <FiX size={9} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  className="rql-settings__tag-input"
                  placeholder={!(draft.newLead.tags?.length) ? 'Type tag name, press Enter or comma to add...' : 'Add another tag...'}
                  value={newLeadTagInput}
                  onChange={e => {
                    const v = e.target.value;
                    if (v.includes(',') || v.includes(';')) { addTag('newLead', v); return; }
                    setNewLeadTagInput(v);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); addTag('newLead', newLeadTagInput); }
                    if (e.key === 'Backspace' && !newLeadTagInput && (draft.newLead.tags?.length ?? 0) > 0) {
                      removeTag('newLead', (draft.newLead.tags?.length ?? 1) - 1);
                    }
                  }}
                  onBlur={() => { if (newLeadTagInput.trim()) addTag('newLead', newLeadTagInput); }}
                  onPaste={e => { const text = e.clipboardData.getData('text'); if (text.includes(',') || text.includes(';')) { e.preventDefault(); addTag('newLead', text); } }}
                />
              </div>
              {newLeadHasTag ? (
                <div className="rql-settings__tag-banner rql-settings__tag-banner--filtered">
                  <FiFilter size={11} />
                  <div>
                    <strong>Filtered mode</strong>
                    <span>Only new prospects whose NowCerts tags match {(draft.newLead.tags ?? []).map(t => `"${t}"`).join(' or ')} will be auto-quoted. All other prospects will be skipped.</span>
                  </div>
                </div>
              ) : (
                <div className="rql-settings__tag-banner rql-settings__tag-banner--all">
                  <FiAlertTriangle size={11} />
                  <div>
                    <strong>No filter — all prospects</strong>
                    <span>Every new prospect will be automatically quoted without any tag filtering. Add tags above to limit which prospects are processed.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="rql-settings__box-divider" />

        {/* Renewal row */}
        <div className="rql-settings__box-row">
          <span className="rql-settings__box-row-icon rql-settings__box-row-icon--renewal"><FiRefreshCw size={11} /></span>
          <div className="rql-settings__box-row-text">
            <span className="rql-settings__box-row-label">Renewal</span>
            <span className="rql-settings__box-row-hint">
              {draft.renewal.enabled ? `Daily at ${draft.renewal.autoRunTime || '08:00'}` : 'Auto-quote expiring policies'}
            </span>
          </div>
          <span
            className={`rql-settings__switch${draft.renewal.enabled ? ' rql-settings__switch--on' : ''}`}
            onClick={() => setDraft(d => ({ ...d, renewal: { ...d.renewal, enabled: !d.renewal.enabled } }))}
          >
            <input type="checkbox" checked={draft.renewal.enabled} onChange={() => {}} style={{ pointerEvents: 'none' }} />
            <span className="rql-settings__switch-thumb" />
          </span>
        </div>

        {/* Renewal config — only when enabled */}
        {draft.renewal.enabled && (
          <div className="rql-settings__box-expand">
            <div className="rql-settings__field">
              <div className="rql-settings__field-left">
                <span className="rql-settings__field-label">Execute before expiry</span>
                <span className="rql-settings__field-hint">Days ahead of expiry to auto-run.</span>
              </div>
              <div className="rql-settings__field-control">
                <input type="number" min={1} max={365} value={draft.renewal.daysBeforeExpiry}
                  onChange={e => setDraft(d => ({ ...d, renewal: { ...d.renewal, daysBeforeExpiry: Math.max(1, Math.min(365, Number(e.target.value) || 0)) } }))}
                  className="rql-settings__input rql-settings__input--num" />
                <span className="rql-settings__field-suffix">days</span>
              </div>
            </div>
            <div className="rql-settings__field">
              <div className="rql-settings__field-left">
                <span className="rql-settings__field-label">Run time</span>
                <span className="rql-settings__field-hint">Time of day the job fires.</span>
              </div>
              <div className="rql-settings__field-control">
                <input type="time" value={draft.renewal.autoRunTime}
                  onChange={e => setDraft(d => ({ ...d, renewal: { ...d.renewal, autoRunTime: e.target.value } }))}
                  className="rql-settings__input" />
                <span className="rql-settings__field-suffix">daily</span>
              </div>
            </div>

            {/* Renewal — optional tag filter */}
            <div className="rql-settings__tag-section">
              <div className="rql-settings__tag-label">
                <FiFilter size={11} />
                <span>Filter by NowCerts Tags</span>
                <span className="rql-settings__tag-badge">Recommended</span>
              </div>
              <span className="rql-settings__tag-desc">
                Add tags to control which renewals are auto-quoted. Tags must match the insured's tags in NowCerts AMS.
              </span>
              <div className="rql-settings__tag-box">
                {(draft.renewal.tags ?? []).map((tag, i) => (
                  <span key={i} className="rql-settings__tag-chip">
                    {tag}
                    <button type="button" className="rql-settings__tag-chip-x" onClick={() => removeTag('renewal', i)}>
                      <FiX size={9} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  className="rql-settings__tag-input"
                  placeholder={!(draft.renewal.tags?.length) ? 'Type tag name, press Enter or comma to add...' : 'Add another tag...'}
                  value={renewalTagInput}
                  onChange={e => {
                    const v = e.target.value;
                    if (v.includes(',') || v.includes(';')) { addTag('renewal', v); return; }
                    setRenewalTagInput(v);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); addTag('renewal', renewalTagInput); }
                    if (e.key === 'Backspace' && !renewalTagInput && (draft.renewal.tags?.length ?? 0) > 0) {
                      removeTag('renewal', (draft.renewal.tags?.length ?? 1) - 1);
                    }
                  }}
                  onBlur={() => { if (renewalTagInput.trim()) addTag('renewal', renewalTagInput); }}
                  onPaste={e => { const text = e.clipboardData.getData('text'); if (text.includes(',') || text.includes(';')) { e.preventDefault(); addTag('renewal', text); } }}
                />
              </div>
              {renewalHasTag ? (
                <div className="rql-settings__tag-banner rql-settings__tag-banner--filtered">
                  <FiFilter size={11} />
                  <div>
                    <strong>Filtered mode</strong>
                    <span>Only renewals whose NowCerts tags match {(draft.renewal.tags ?? []).map(t => `"${t}"`).join(' or ')} will be auto-quoted. All other renewals will be skipped.</span>
                  </div>
                </div>
              ) : (
                <div className="rql-settings__tag-banner rql-settings__tag-banner--all">
                  <FiAlertTriangle size={11} />
                  <div>
                    <strong>No filter — all renewals</strong>
                    <span>Every expiring policy will be automatically quoted without any tag filtering. Add tags above to limit which renewals are processed.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ═══ BOX 2 — Voice Outreach ═══ */}
      <div className="rql-settings__box rql-settings__box--voice">
        <div className="rql-settings__box-header rql-settings__box-header--voice">
          <span className="rql-settings__box-header-icon rql-settings__box-header-icon--voice"><FiPhone size={13} /></span>
          <div className="rql-settings__box-header-title">Voice Outreach</div>
          <span className="rql-settings__voice-badge">AI</span>
        </div>

        {/* Voice Agent row */}
        <div className="rql-settings__box-row">
          <span className="rql-settings__box-row-icon rql-settings__box-row-icon--voice"><FiPhone size={11} /></span>
          <div className="rql-settings__box-row-text">
            <span className="rql-settings__box-row-label">Voice Agent</span>
            <span className="rql-settings__box-row-hint">
              {draft.voice?.enabled ? 'Calls new leads every 12 hours' : 'Auto-call via Momentum Voice'}
            </span>
          </div>
          <span
            className={`rql-settings__switch${draft.voice?.enabled ? ' rql-settings__switch--voice-on' : ''}`}
            onClick={() => setDraft(d => ({
              ...d,
              voice: { ...(d.voice ?? { agentId: null, agentName: null, agentPhoneNumberId: null }), enabled: !d.voice?.enabled },
            }))}
          >
            <input type="checkbox" checked={!!draft.voice?.enabled} onChange={() => {}} style={{ pointerEvents: 'none' }} />
            <span className="rql-settings__switch-thumb" />
          </span>
        </div>

        {/* Voice config — only when enabled */}
        {draft.voice?.enabled && (
          <div className="rql-settings__box-expand">
            <div className="rql-settings__voice-agent-row">
              {voiceLoading ? (
                <span className="rql-skel rql-skel--md" style={{ height: 48, borderRadius: 10 }} />
              ) : (
                <>
                  <div
                    className={`rql-settings__vagent-trigger${agentDropOpen ? ' rql-settings__vagent-trigger--open' : ''}${draft.voice.agentId ? ' rql-settings__vagent-trigger--has' : ''}`}
                    onClick={() => setAgentDropOpen(o => !o)}
                  >
                    {draft.voice.agentId && voiceAgents.find(a => a.elevenLabsAgentId === draft.voice?.agentId) ? (() => {
                      const sa = voiceAgents.find(a => a.elevenLabsAgentId === draft.voice?.agentId)!;
                      const sn = phoneNumbers.find(n => n.assignedAgentId != null && String(n.assignedAgentId) === sa.elevenLabsAgentId);
                      return (<>
                        <div className="rql-settings__vagent-trigger-icon"><FiPhone size={13} /></div>
                        <div className="rql-settings__vagent-trigger-info">
                          <span className="rql-settings__vagent-trigger-name">{sa.agentName}</span>
                          <span className="rql-settings__vagent-trigger-phone">
                            {sn ? <><FiPhone size={8} /> {sn.phoneNumber ?? 'Assigned'}</> : 'No phone assigned'}
                          </span>
                        </div>
                      </>);
                    })() : (
                      <span className="rql-settings__vagent-trigger-placeholder">Select a voice agent...</span>
                    )}
                    <FiChevronDown size={14} className={`rql-settings__vagent-trigger-chevron${agentDropOpen ? ' rql-settings__vagent-trigger-chevron--up' : ''}`} />
                  </div>

                  {agentDropOpen && (
                    <div className="rql-settings__vagent-dropdown">
                      {voiceAgents.map(a => {
                        const agentElId = a.elevenLabsAgentId ?? '';
                        const isActive = draft.voice!.agentId === agentElId;
                        const aNum = phoneNumbers.find(n => n.assignedAgentId != null && String(n.assignedAgentId) === agentElId);
                        return (
                          <div key={agentElId || String(a.id)}
                            className={`rql-settings__vagent-option${isActive ? ' rql-settings__vagent-option--active' : ''}`}
                            onClick={() => { setDraft(d => ({ ...d, voice: { ...d.voice!, agentId: agentElId || null, agentName: a.agentName ?? null, agentPhoneNumberId: aNum?.phoneNumberId ?? null } })); setAgentDropOpen(false); }}>
                            <span className="rql-settings__vagent-option-name">{a.agentName ?? `Agent #${a.id}`}</span>
                            <span className={`rql-settings__vagent-option-phone${aNum ? '' : ' rql-settings__vagent-option-phone--none'}`}>
                              {aNum ? <><FiPhone size={8} /> {aNum.phoneNumber ?? aNum.label ?? 'Assigned'}</> : 'No phone'}
                            </span>
                            {isActive && <FiCheck size={12} className="rql-settings__vagent-option-check" />}
                          </div>
                        );
                      })}
                      {voiceAgents.length === 0 && (
                        <div className="rql-settings__vagent-option rql-settings__vagent-option--empty">No agents found — create one in Toolbox</div>
                      )}
                    </div>
                  )}

                  {draft.voice.agentId && draft.voice.agentPhoneNumberId && (
                    <div className="rql-settings__voice-status rql-settings__voice-status--ok"><FiCheck size={11} /><span>Ready to call</span></div>
                  )}
                  {draft.voice.agentId && !draft.voice.agentPhoneNumberId && (
                    <div className="rql-settings__voice-status rql-settings__voice-status--warn"><FiAlertTriangle size={11} /><span>Assign a phone number in Toolbox</span></div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ═══ Summary + Consent ═══ */}
      {needsConsent && (
        <div className="rql-settings__consent-wrapper">
          <div className="rql-settings__consent-summary">
            <div className="rql-settings__consent-summary-title">
              <FiAlertTriangle size={12} />
              <span>What will happen when you save</span>
            </div>
            <ul className="rql-settings__consent-list">
              {draft.newLead.enabled && (
                <li>
                  <strong>New Lead:</strong>{' '}
                  {newLeadHasTag
                    ? <>Only prospects tagged {(draft.newLead.tags ?? []).map(t => `"${t}"`).join(', ')} in NowCerts will be auto-quoted every 3 hours.</>
                    : <>All new prospects will be auto-quoted every 3 hours without any tag filter.</>
                  }
                </li>
              )}
              {draft.renewal.enabled && (
                <li>
                  <strong>Renewal:</strong>{' '}
                  {renewalHasTag
                    ? <>Only renewals tagged {(draft.renewal.tags ?? []).map(t => `"${t}"`).join(', ')} in NowCerts will be auto-quoted daily at {draft.renewal.autoRunTime || '08:00'}.</>
                    : <>All expiring policies will be auto-quoted daily at {draft.renewal.autoRunTime || '08:00'} without any tag filter.</>
                  }
                </li>
              )}
              {draft.voice?.enabled && (
                <li><strong>Voice:</strong> Outbound calls will be placed to new leads automatically.</li>
              )}
              <li>Carrier submissions and API usage will occur on a recurring schedule without manual review.</li>
            </ul>
          </div>
          <label className="rql-settings__consent" onClick={() => setConsent(c => !c)}>
            <span className={`rql-settings__consent-check${consent ? ' rql-settings__consent-check--on' : ''}`}>
              {consent && <FiCheck size={10} />}
            </span>
            <span className="rql-settings__consent-text">
              I have reviewed the above and confirm I want to enable this automation.
            </span>
          </label>
        </div>
      )}

      <div className="rql-settings-inline__footer">
        <button type="button" className="rql-settings__btn rql-settings__btn--ghost"
          onClick={() => { setDraft(settings); setConsent(false); setPanelView('workflow'); }}>
          Cancel
        </button>
        <button type="button"
          className={`rql-settings__btn rql-settings__btn--primary${savedFlash ? ' is-saved' : ''}`}
          onClick={onSave}
          disabled={(!dirty && !savedFlash) || (needsConsent && !consent)}>
          {savedFlash ? <><FiCheck size={13} /> Saved!</> : <><FiCheck size={13} /> Save changes</>}
        </button>
      </div>
    </div>
  );
};


/* ══════════════════════════════════════════════════════════════════════════
   QUOTE DETAIL VIEW — full submitted quote run opened from row history
   ══════════════════════════════════════════════════════════════════════════ */

const CARRIER_STATUS_COLOR: Record<string, string> = {
  quoted:   '#34d399',
  declined: '#94a3b8',
  failed:   '#ef4444',
};

const QuoteDetailView: React.FC = () => {
  const { selectedQuoteRun, setSelectedQuoteRun, setPanelView } = useRateQuoteLink();

  if (!selectedQuoteRun) {
    return (
      <div className="rql-qd-empty">
        <FiFileText size={26} style={{ opacity: 0.35, marginBottom: 10 }} />
        <div className="rql-qd-empty__title">No quote run selected</div>
        <div className="rql-qd-empty__hint">
          Click a Quote run block in the expanded row history to see its full details here.
        </div>
      </div>
    );
  }

  const run = selectedQuoteRun;
  const quoted   = run.carriers.filter(c => c.status === 'quoted');
  const failed   = run.carriers.filter(c => c.status !== 'quoted');
  const lowest   = quoted.length ? Math.min(...quoted.map(c => c.premiumRaw ?? Infinity)) : null;
  const allDeclined = run.carriers.length > 0 && quoted.length === 0;

  // Renewal delta helpers
  const bestQuoted = quoted.length
    ? quoted.reduce<typeof quoted[0] | null>((b, c) => {
        if (!b) return c;
        return (c.premiumRaw ?? Infinity) < (b.premiumRaw ?? Infinity) ? c : b;
      }, null)
    : null;
  const showDelta = run.isRenewal && run.currentPremium != null && bestQuoted != null;
  const savings = showDelta && bestQuoted?.premiumRaw != null
    ? run.currentPremium! - bestQuoted.premiumRaw
    : null;
  const savingsPct = showDelta && savings != null && run.currentPremium
    ? Math.round((savings / run.currentPremium) * 100)
    : null;

  return (
    <div className="rql-qd">
      {/* ── Back button ── */}
      <button
        type="button"
        className="rql-qd__back"
        onClick={() => { setSelectedQuoteRun(null); setPanelView('details'); }}
      >
        <FiArrowLeft size={11} />
        Back to details
      </button>

      {/* ── Run header ── */}
      <div className="rql-qd__header">
        <div className="rql-qd__header-left">
          <span className="rql-qd__run-label">
            {run.runNumber != null ? `Quote #${run.runNumber}` : 'Quote Run'}
          </span>
          <span className={`rql-qd__status rql-qd__status--${run.runStatus}`}>
            {run.runStatus === 'completed' && <FiCheck size={9} />}
            {run.runStatus === 'failed'    && <FiAlertTriangle size={9} />}
            {run.runStatus === 'completed' ? 'Completed' : run.runStatus === 'failed' ? 'Failed' : run.runStatus}
          </span>
        </div>
        <div className="rql-qd__meta">
          <span className="rql-qd__date"><FiCalendar size={10} /> {run.executedAt}</span>
          {run.durationMs != null && (
            <span className="rql-qd__dur"><FiClock size={10} /> {(run.durationMs / 1000).toFixed(1)}s</span>
          )}
        </div>
      </div>

      {/* ── Submission summary chips ── */}
      {(run.submittedDriversCount != null || run.submittedVehiclesCount != null) && (
        <div className="rql-qd__chips">
          {run.submittedDriversCount != null && (
            <span className="rql-qd__chip"><FiUser size={10} /> {run.submittedDriversCount} driver{run.submittedDriversCount !== 1 ? 's' : ''}</span>
          )}
          {run.submittedVehiclesCount != null && (
            <span className="rql-qd__chip"><FiFileText size={10} /> {run.submittedVehiclesCount} vehicle{run.submittedVehiclesCount !== 1 ? 's' : ''}</span>
          )}
          <span className="rql-qd__chip">{run.carriers.length} carrier{run.carriers.length !== 1 ? 's' : ''} rated</span>
          {quoted.length > 0 && (
            <span className="rql-qd__chip rql-qd__chip--ok"><FiCheck size={9} /> {quoted.length} quoted</span>
          )}
        </div>
      )}

      {/* ── Renewal premium delta card ── */}
      {showDelta && bestQuoted && (
        <div className={`rql-delta-card${savings != null && savings > 0 ? ' rql-delta-card--savings' : savings != null && savings < 0 ? ' rql-delta-card--increase' : ''}`}>
          <div className="rql-delta-card__row">
            <span className="rql-delta-card__label">Current ({run.currentCarrier || 'Carrier'})</span>
            <span className="rql-delta-card__value">
              ${run.currentPremium!.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/yr
            </span>
          </div>
          <div className="rql-delta-card__arrow">↓</div>
          <div className="rql-delta-card__row rql-delta-card__row--best">
            <span className="rql-delta-card__label">Best new quote ({bestQuoted.name})</span>
            <span className="rql-delta-card__value rql-delta-card__value--best">
              ${bestQuoted.premiumRaw!.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/yr
            </span>
          </div>
          {savings != null && savingsPct != null && (
            <div className={`rql-delta-card__summary${savings > 0 ? ' rql-delta-card__summary--save' : ' rql-delta-card__summary--increase'}`}>
              {savings > 0
                ? <><FiTrendingUp size={11} /> Saves ${savings.toLocaleString()}/yr ({savingsPct}% reduction)</>
                : <><FiAlertTriangle size={11} /> ${Math.abs(savings).toLocaleString()}/yr more than current ({Math.abs(savingsPct)}% increase)</>}
            </div>
          )}
        </div>
      )}

      {/* ── All carriers declined banner ── */}
      {allDeclined && (
        <div className="rql-qd__all-declined">
          <div className="rql-qd__all-declined-head">
            <FiAlertTriangle size={12} />
            <span>All carriers declined or failed — no quotes returned</span>
          </div>
          <div className="rql-qd__all-declined-reasons">
            {run.carriers.map((c, ci) => (
              <div key={ci} className="rql-qd__all-declined-row">
                <span className="rql-qd__all-declined-name">{c.name}</span>
                <span className={`rql-qd__all-declined-badge rql-qd__all-declined-badge--${c.status}`}>
                  {c.status === 'declined' ? 'Declined' : 'Failed'}
                </span>
                {c.failureReason && (
                  <span className="rql-qd__all-declined-reason">{c.failureReason}</span>
                )}
              </div>
            ))}
          </div>
          <div className="rql-qd__all-declined-hint">
            <FiChevronRight size={10} />
            Try adjusting coverages or effective date and re-run the workflow.
          </div>
        </div>
      )}

      {/* ── Carrier results ── */}
      {run.carriers.length > 0 && (
        <div className="rql-qd__section">
          <div className="rql-qd__section-title">Carrier Results</div>
          <div className="rql-qd__carriers">
            {[...run.carriers]
              .sort((a, b) => {
                if (a.status !== b.status) return a.status === 'quoted' ? -1 : 1;
                return (a.premiumRaw ?? Infinity) - (b.premiumRaw ?? Infinity);
              })
              .map((c, ci) => {
                const isLowest = c.status === 'quoted' && c.premiumRaw === lowest && quoted.length > 1;
                return (
                  <div key={ci} className={`rql-qd__carrier rql-qd__carrier--${c.status}${isLowest ? ' rql-qd__carrier--best' : ''}`}>
                    {isLowest && <span className="rql-qd__best-badge">Best Price</span>}
                    <div className="rql-qd__carrier-hdr">
                      {c.logoURL ? (
                        <img src={c.logoURL} alt={c.name} className="rql-qd__carrier-logo" />
                      ) : (
                        <div className="rql-qd__carrier-initial">{c.name.charAt(0).toUpperCase()}</div>
                      )}
                      <div className="rql-qd__carrier-info">
                        <div className="rql-qd__carrier-name">{c.name}</div>
                        {c.amBestRating && (
                          <div className="rql-qd__carrier-rating">AM Best: {c.amBestRating}</div>
                        )}
                      </div>
                      <div className="rql-qd__carrier-right">
                        <span
                          className="rql-qd__carrier-status"
                          style={{ color: CARRIER_STATUS_COLOR[c.status] ?? '#94a3b8' }}
                        >
                          {c.status === 'quoted' ? <FiCheck size={10} /> : <FiAlertTriangle size={10} />}
                          {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                        </span>
                        {c.status === 'quoted' && (
                          <div className="rql-qd__carrier-premium">{c.premium}<span className="rql-qd__carrier-term">/{c.termLabel}</span></div>
                        )}
                        {c.status === 'quoted' && c.monthlyPremium !== '—' && (
                          <div className="rql-qd__carrier-monthly">{c.monthlyPremium}/mo</div>
                        )}
                      </div>
                    </div>
                    {c.status !== 'quoted' && c.failureReason && (
                      <div className="rql-qd__carrier-reason">
                        <FiAlertTriangle size={9} /> {c.failureReason}
                      </div>
                    )}
                    {c.bridgingLink && c.status === 'quoted' && (
                      <a
                        href={c.bridgingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rql-qd__bind-btn"
                        onClick={e => e.stopPropagation()}
                      >
                        Bind with carrier <FiChevronRight size={10} />
                      </a>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* ── Submitted coverages ── */}
      {run.rawCoverages && run.rawCoverages.length > 0 && (
        <div className="rql-qd__section">
          <div className="rql-qd__section-title">Submitted Coverages</div>
          <dl className="rql-qd__cov-grid">
            {run.rawCoverages.map((c, ci) => (
              <React.Fragment key={ci}>
                <dt className="rql-qd__cov-key">{c.key.replace(/_/g, ' ')}</dt>
                <dd className="rql-qd__cov-val">{c.value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      )}

      {/* ── Error ── */}
      {run.runStatus === 'failed' && run.errorMessage && (
        <div className="rql-qd__error">
          <FiAlertTriangle size={12} />
          <span>{run.errorMessage}</span>
        </div>
      )}
    </div>
  );
};

/* ── Voice Call History View ────────────────────────────────────────────────── */

const VoiceHistoryView: React.FC = () => {
  const [calls, setCalls] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchCalls = useCallback(() => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) { setLoading(false); return; }
    setLoading(true);
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting/voice-calls`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(json => {
        const arr = Array.isArray(json) ? json : Array.isArray(json?.calls) ? json.calls : [];
        setCalls(arr);
      })
      .catch(() => setCalls([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const statusBadge = (c: any) => {
    if (c.interested === true) return <span className="rql-voice-hist__badge rql-voice-hist__badge--interested">Interested</span>;
    if (c.interested === false) return <span className="rql-voice-hist__badge rql-voice-hist__badge--not-interested">Not interested</span>;
    if (c.call_status === 'calling') return <span className="rql-voice-hist__badge rql-voice-hist__badge--calling">Calling...</span>;
    if (c.call_status === 'failed') return <span className="rql-voice-hist__badge rql-voice-hist__badge--failed">Failed</span>;
    if (c.call_status === 'completed') return <span className="rql-voice-hist__badge rql-voice-hist__badge--completed">Completed</span>;
    return <span className="rql-voice-hist__badge rql-voice-hist__badge--pending">{c.call_status || 'Pending'}</span>;
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return d; }
  };

  return (
    <div className="rql-voice-hist">
      <div className="rql-voice-hist__header">
        <span className="rql-voice-hist__title">Voice Call History</span>
        <button type="button" className="rql-voice-hist__refresh" onClick={fetchCalls} title="Refresh">
          <FiRefreshCw size={13} />
        </button>
      </div>

      {loading ? (
        <div className="rql-voice-hist__loading">
          {[1,2,3].map(i => <div key={i} className="rql-voice-hist__skel" />)}
        </div>
      ) : calls.length === 0 ? (
        <div className="rql-voice-hist__empty">
          <FiPhone size={20} style={{ opacity: 0.3, marginBottom: 8 }} />
          <span>No voice calls yet</span>
          <span style={{ fontSize: 11, opacity: 0.5 }}>Calls placed from the prospects table or automated routine will appear here.</span>
        </div>
      ) : (
        <div className="rql-voice-hist__list">
          {calls.map(c => {
            const isExpanded = expandedId === c.id;
            return (
              <div key={c.id} className={`rql-voice-hist__card${isExpanded ? ' rql-voice-hist__card--open' : ''}`}>
                <div className="rql-voice-hist__card-head" onClick={() => setExpandedId(isExpanded ? null : c.id)}>
                  <div className="rql-voice-hist__card-left">
                    <span className="rql-voice-hist__card-name">{c.insured_name || '—'}</span>
                    <span className="rql-voice-hist__card-date">{formatDate(c.called_at || c.created_at)}</span>
                  </div>
                  <div className="rql-voice-hist__card-right">
                    {statusBadge(c)}
                    <FiChevronDown size={12} className={`rql-voice-hist__card-chevron${isExpanded ? ' rql-voice-hist__card-chevron--up' : ''}`} />
                  </div>
                </div>

                {isExpanded && (
                  <div className="rql-voice-hist__card-body">
                    <div className="rql-voice-hist__row"><span>Phone</span><span>{c.phone_number || '—'}</span></div>
                    <div className="rql-voice-hist__row"><span>Call SID</span><span style={{ fontSize: 10, opacity: 0.6 }}>{c.call_sid || '—'}</span></div>
                    <div className="rql-voice-hist__row"><span>Status</span><span>{c.call_status || '—'}</span></div>
                    <div className="rql-voice-hist__row"><span>Interested</span><span>{c.interested === true ? 'Yes' : c.interested === false ? 'No' : '—'}</span></div>
                    {c.quote_run_id && (
                      <div className="rql-voice-hist__row"><span>Quote Run</span><span style={{ color: '#4ED56E', fontSize: 11 }}>{c.quote_run_id}</span></div>
                    )}
                    {c.transcript_summary && (
                      <div className="rql-voice-hist__summary">
                        <span className="rql-voice-hist__summary-label">Summary</span>
                        <p>{c.transcript_summary}</p>
                      </div>
                    )}
                    {c.completed_at && (
                      <div className="rql-voice-hist__row"><span>Completed</span><span>{formatDate(c.completed_at)}</span></div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface TabDef {
  id: PanelView;
  label: string;
  Icon: React.ComponentType<{ size?: number }>;
}

// Agencies allowed to see the Settings tab (comma-separated in env var).
// If not set or empty, Settings is hidden for all agencies.
const _PLR_SETTINGS_AGENCIES = (process.env.REACT_APP_PLR_Settings_AgencyIds || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);

function isPLRSettingsAllowed(): boolean {
  if (_PLR_SETTINGS_AGENCIES.length === 0) return false;
  if (_PLR_SETTINGS_AGENCIES.includes('*')) return true;
  const agencyId = (localStorage.getItem('NCAgencyId') || '').toLowerCase();
  return !!agencyId && _PLR_SETTINGS_AGENCIES.includes(agencyId);
}

const TABS_BASE: TabDef[] = [
  { id: 'details',  label: 'Details',  Icon: FiFileText },
];
const TAB_SETTINGS: TabDef = { id: 'settings', label: 'Settings', Icon: FiSettings };

const TABS: TabDef[] = isPLRSettingsAllowed()
  ? [...TABS_BASE, TAB_SETTINGS]
  : TABS_BASE;

const VIEW_TITLE: Record<PanelView, { eyebrow: string; title: string }> = {
  details:       { eyebrow: 'Workspace',    title: 'Quote details' },
  workflow:      { eyebrow: 'Workspace',    title: 'Workflow details' },
  history:       { eyebrow: 'Workspace',    title: 'History & insights' },
  settings:      { eyebrow: 'Workspace',    title: 'Automation settings' },
  'quote-detail':   { eyebrow: 'Quote Run',   title: 'Submitted quote details' },
  'voice-history':  { eyebrow: 'Voice',       title: 'Call history' },
};

const RateQuoteLinkSidebarPanel: React.FC = () => {
  const { panelView, setPanelView, isRunning, history, historyRuns, selectedRow } = useRateQuoteLink();
  const historyCount = (historyRuns ?? history).length;
  const title = VIEW_TITLE[panelView];

  return (
    <div className="rql-run-panel">
      <div className="rql-tabstrip" role="tablist" aria-label="Workspace views">
        {TABS.map(tab => {
          const active = panelView === tab.id;
          const showLive = tab.id === 'workflow' && isRunning;
          const showCount = tab.id === 'history' && historyCount > 0;
          const showSelDot = tab.id === 'details' && selectedRow;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={active}
              type="button"
              className={`rql-tabstrip__btn${active ? ' rql-tabstrip__btn--active' : ''}`}
              onClick={() => setPanelView(tab.id)}
              title={tab.label}
            >
              <tab.Icon size={13} />
              <span className="rql-tabstrip__label">{tab.label}</span>
              {showLive && <span className="rql-tabstrip__live" />}
              {showCount && <span className="rql-tabstrip__count">{historyCount}</span>}
              {showSelDot && <span className="rql-tabstrip__sel" />}
            </button>
          );
        })}
      </div>

      <div className="rql-run-panel__title-row">
        <div className="rql-run-panel__title">
          <span>{title.eyebrow}</span>
          <span className="rql-run-panel__title-sep" />
          <span className="rql-run-panel__title-strong">{title.title}</span>
        </div>
      </div>

      <div className="rql-view">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={panelView}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="rql-view__inner"
          >
            {panelView === 'details'       && <DetailsView />}
            {panelView === 'workflow'      && <WorkflowView />}
            {panelView === 'history'       && <HistoryView />}
            {panelView === 'settings'      && <SettingsView />}
            {panelView === 'quote-detail'  && <QuoteDetailView />}
            {/* voice-history removed from sidebar — shown as per-prospect popup in table */}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default RateQuoteLinkSidebarPanel;

